import { clearAnalyticsCookies, readConsent } from "./consent";
import { calculateDiscountedPrice } from "./utils/format";

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    dataLayer?: unknown[];
  }
}

/**
 * The measurement ID is configuration, not a constant (EB-558).
 *
 * It used to be written into `public/index.html`, which meant every environment that ran the app —
 * localhost, both Vercel projects, and the Playwright suite — reported into the production
 * property. The e2e suite drives a real checkout, so it was manufacturing `purchase` hits, and no
 * conversion figure in GA could be trusted.
 *
 * Read once at module load: CRA inlines `REACT_APP_*` at build time, so this is a literal in the
 * bundle and cannot change at runtime.
 */
const MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID;

/** Analytics is opt-in: with no measurement ID configured, gtag is never loaded and every call
 *  below is a no-op. Silence is the safe default — a dev or test run that reports nothing is a
 *  nuisance, one that reports into production is corrupted data nobody can retract. */
export const isAnalyticsEnabled = (): boolean => Boolean(MEASUREMENT_ID);

/** The Consent Mode v2 signals, all four denied. Only `analytics_storage` is ever granted here —
 *  the site runs no advertising — but the ad signals have to be *declared* rather than omitted, or
 *  Google applies its own regional defaults instead of ours. */
const DENIED_CONSENT = {
  analytics_storage: "denied",
  ad_storage: "denied",
  ad_user_data: "denied",
  ad_personalization: "denied",
} as const;

const TAG_SCRIPT_ID = "ga-gtag";

/** Loads gtag.js, once. */
const loadTagScript = () => {
  if (!MEASUREMENT_ID || document.getElementById(TAG_SCRIPT_ID)) return;

  const script = document.createElement("script");
  script.id = TAG_SCRIPT_ID;
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(MEASUREMENT_ID)}`;
  document.head.appendChild(script);
};

/**
 * Installs the gtag bootstrap and loads the tag, with **all consent signals denied by default**
 * (EB-559, adjusted in EB-560).
 *
 * `window.gtag` and `window.dataLayer` are defined **synchronously**, before any network request —
 * that is what the official snippet does, and it is what makes it safe to call `gaEvent` during the
 * first render. Commands queue in `dataLayer` and the tag replays them once it arrives.
 *
 * The order is not negotiable: `consent default` comes **before** `js` and `config`. Consent Mode
 * only constrains commands issued after it, so a default set even one line after `config` is a
 * default that arrived too late to constrain the thing it exists to constrain.
 *
 * **What denied consent does and does not do.** No `_ga` cookie is written and no browser
 * identifier is generated, so nobody can be recognised across visits — which is the part Colombian
 * law is about, since the SIC's test for a cookie needing authorisation is whether it lets a user
 * be identified or associated. Google *does* still receive a cookieless ping carrying the IP and
 * the page. EB-559 deferred the script entirely to avoid even that; EB-560 relaxed it, because
 * Colombia has no ePrivacy-style rule forbidding the transmission itself and matching Google's
 * documented behaviour is less to maintain. **§10 of the privacy policy states this distinction
 * explicitly, and the two have to stay in step** — the whole point of EB-559 was that they had
 * drifted apart.
 */
export const initAnalytics = () => {
  if (!MEASUREMENT_ID || window.gtag) return;

  window.dataLayer = window.dataLayer || [];

  // Pushes the `arguments` object rather than a rest array on purpose: gtag.js inspects the
  // arguments-like shape of each queued command, and this is the one detail of Google's snippet
  // that is load-bearing rather than stylistic.
  const gtag: NonNullable<Window["gtag"]> = function () {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer?.push(arguments);
  };
  window.gtag = gtag;

  gtag("consent", "default", DENIED_CONSENT);
  gtag("js", new Date());
  gtag("config", MEASUREMENT_ID, { send_page_view: false });

  // Restores a stored acceptance before the tag arrives, so a returning shopper's first page view
  // of the session is measured rather than dropped.
  if (readConsent() === "granted") {
    gtag("consent", "update", { ...DENIED_CONSENT, analytics_storage: "granted" });
  }

  loadTagScript();
};

/**
 * Applies a consent decision to a session already running (EB-559).
 *
 * Granting takes effect without a reload: the tag is already loaded, so flipping the signal is
 * enough for the queued `config` and any `page_view` already fired to be measured.
 *
 * Denying flips the signal *and* deletes the cookies. Consent Mode alone would leave `_ga` on the
 * device after someone changed their mind, and a shopper who checks would find the thing they just
 * refused still sitting there.
 */
export const applyAnalyticsConsent = (granted: boolean) => {
  window.gtag?.("consent", "update", {
    ...DENIED_CONSENT,
    ...(granted ? { analytics_storage: "granted" } : {}),
  });

  if (!granted) clearAnalyticsCookies();
};

/**
 * `page_path` is the canonical page identity and carries **no query string** (EB-558) — the search
 * term lives in `?q=` and a refined search is not a new page. `page_location` keeps the full URL
 * because GA reads campaign parameters (`utm_*`, `gclid`) off it on the session's first hit;
 * stripping the query here would canonicalise the path at the cost of every campaign attribution.
 */
export const pageview = (path: string, params: Record<string, unknown> = {}) => {
  window.gtag?.("event", "page_view", {
    page_title: document.title,
    page_location: window.location.href,
    page_path: path,
    ...params,
  });
};

export const gaEvent = (name: string, params: Record<string, unknown> = {}) =>
  window.gtag?.("event", name, params);

export type LoginState = "logged_in" | "anonymous";

let loginState: LoginState = "anonymous";

/**
 * Who is looking, for segmentation (EB-558).
 *
 * Two mechanisms, deliberately:
 *
 * - **`user_id`** is GA4's own User-ID feature, so a shopper who browses on a phone and orders on a
 *   laptop is one person rather than two sessions. The value is the Auth0 `sub` — an opaque
 *   identifier, not an email or a name, which is what makes it safe to send.
 * - **`login_state` as a user property**, not an event parameter. A property attaches to every
 *   subsequent event, so the dimension is available on `purchase` and `view_item` alike without
 *   each call site remembering to add it — and remembering is exactly what call sites do not do.
 *
 * Passing `null` clears the id, which is what a logout has to do: gtag keeps the last value it was
 * given, so a shared machine would otherwise attribute the next visitor's session to whoever
 * logged in before them.
 */
export const setAnalyticsUser = (userId: string | null) => {
  loginState = userId ? "logged_in" : "anonymous";
  window.gtag?.("set", { user_id: userId });
  window.gtag?.("set", "user_properties", { login_state: loginState });
};

/** Exposed for tests and for callers that need the state as a parameter rather than a property. */
export const getLoginState = (): LoginState => loginState;

/**
 * The shape every GA4 e-commerce event needs an `items[]` entry to have.
 *
 * `id` is optional because `ItemType.id` is: a product being created in the admin form has no id
 * yet. GA4 accepts an entry carrying only `item_name`, which is why the key is omitted below rather
 * than sent as an empty string — an `item_id: ""` would collide every such row into one product.
 */
export type GaItemSource = {
  id?: string;
  name: string;
  price?: number;
  discount?: number;
  tipo_producto?: string;
};

/** The list price as a number. `ItemType.price` is typed `number` but arrives from the API, where a
 *  hand-edited row can hold a numeric string. */
const gaPrice = (item: GaItemSource): number =>
  typeof item.price === "number" ? item.price : Number(item.price || 0);

/**
 * One place that decides what a product looks like to GA4 (EB-558).
 *
 * GA4 stitches the funnel by matching `items[]` across events, so the entries have to agree. Two
 * decisions are made here rather than at each call site:
 *
 * - **`item_category` comes from `tipo_producto`**, the same field the storefront's own category
 *   facets use. The older call sites hardcode `"general"`, which files every product under one
 *   category and makes the GA dimension useless.
 * - **`price` is the price actually payable**, discount applied, via the same
 *   `calculateDiscountedPrice` the cart and every price label already use. `purchase` reports the
 *   server's discounted total, so a cart-stage event reporting the list price makes the funnel
 *   appear to lose value on every discounted order when nothing was abandoned.
 *
 * Every e-commerce event goes through this — that is what stops them drifting apart again.
 */
export const toGaItem = (
  item: GaItemSource,
  extras: Record<string, unknown> = {},
): Record<string, unknown> => {
  const price = gaPrice(item);
  const discount = item.discount ?? 0;

  return {
    ...(item.id ? { item_id: item.id } : {}),
    item_name: item.name,
    price: calculateDiscountedPrice(price, discount),
    item_category: item.tipo_producto || "general",
    ...(discount > 0 ? { discount: price - calculateDiscountedPrice(price, discount) } : {}),
    ...extras,
  };
};

/**
 * What a set of lines is worth to GA4 — the number that lands in `value` (EB-558).
 *
 * Split out from `toGaItem` because `value` and `items[].price` are computed at different call
 * sites and used to disagree: the item entry would carry a discounted price while `value` summed
 * list prices, so a report reading revenue off `value` and average price off `items[]` gave two
 * different answers for the same event. Deriving both from the same function is the fix.
 *
 * `quantity` matters: GA4 sums `value` into revenue, so a three-unit add reporting the unit price
 * under-reports by two thirds.
 */
export const gaValue = (items: (GaItemSource & { quantity?: number })[]): number =>
  items.reduce(
    (total, item) =>
      total + calculateDiscountedPrice(gaPrice(item), item.discount ?? 0) * (item.quantity ?? 1),
    0,
  );
